/**
 * TOBE-18004 — stacked-state detection for fluid columns rows that carry an
 * empty filler column.
 *
 * Deliberately free of Angular imports: this is the whole decision, so it can be
 * unit-tested in jsdom and exercised verbatim by the headless-Chrome layout
 * harness. `FluidColumnsStackService` only wires observers around it.
 */

/** The vertical extent of one column, in its offsetParent's coordinates. */
export interface ColumnBox {
  top: number;
  height: number;
}

/** Class the formiojs columns template stamps on a componentless column. */
export const COL_EMPTY_CLASS = 'col-empty';

/** Class toggled on a columns row whose real columns have folded. */
export const COLS_STACKED_CLASS = 'cols-stacked';

/**
 * Decides whether a columns row has folded, from the geometry of its REAL
 * (non-filler) columns.
 *
 * A column that starts at or below the reference column's BOTTOM edge is on a
 * later flex line. Comparing bottoms rather than tops matters: the formio
 * `vertical-align-center` / `vertical-align-bottom` classes let columns on the
 * SAME line sit at different offsetTops, and a naive top comparison would read
 * that as a wrap and hide the filler on a row that never folded.
 *
 * The first column is a safe reference under all three alignments — stretch,
 * centre and bottom all keep same-line columns inside the line's box, so a
 * same-line column can never start at or past the reference's bottom, while a
 * next-line column always does.
 */
export function isRowStacked(boxes: ColumnBox[]): boolean {
  if (boxes.length < 2) {
    return false;
  }
  const referenceBottom = boxes[0].top + boxes[0].height;
  return boxes.slice(1).some((box) => box.top >= referenceBottom);
}

/**
 * Reads the geometry of a row's REAL columns.
 *
 * The filler is excluded on purpose, and that exclusion is what makes the whole
 * mechanism stable: the filler's hypothetical main size is already 0, so hiding
 * it cannot change where the real columns break — the class it drives can never
 * flip back and forth.
 */
export function readColumnBoxes(row: HTMLElement): ColumnBox[] {
  const boxes: ColumnBox[] = [];
  for (const child of Array.from(row.children)) {
    const column = child as HTMLElement;
    if (column.classList.contains(COL_EMPTY_CLASS)) {
      continue;
    }
    boxes.push({ top: column.offsetTop, height: column.offsetHeight });
  }
  return boxes;
}

/** Measures a row and applies (or clears) the stacked cue. */
export function applyStackedClass(row: HTMLElement): void {
  row.classList.toggle(COLS_STACKED_CLASS, isRowStacked(readColumnBoxes(row)));
}
